import React from 'react'
import _ from 'lodash'
import clsx from 'clsx'
import { type Control, type FieldError, type FieldErrors, useController, useForm } from 'react-hook-form'
import * as RCheckbox from '@radix-ui/react-checkbox'
import Icon from '@components/utils/Icon'
import { useFormField } from '@hooks/useFormField'

type CheckboxProps = {
  control?: Control<any>
  name: string
  errors?: FieldErrors
  required?: boolean
  // disabled?: boolean
  defaultChecked?: boolean
  // onClick is handled natively by radix Checkbox
  // but when it's rendered as the trigger for another
  // radix component (like Collapsible), it will pass it down
  // to take control of it (no need to do pass it manually if using radix)
  onClick?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void
  children?: React.ReactNode
  description?: string
  tone?: 'primary' | 'danger'
  className?: string
}

const toneClasses = {
  primary: {
    box: 'tw:data-[state=checked]:border-primary-500 tw:focus:border-primary-500 tw:focus:ring-primary-200',
    check: 'tw:text-primary-500',
  },
  danger: {
    box: 'tw:data-[state=checked]:border-error-500 tw:focus:border-error-500 tw:focus:ring-error-200',
    check: 'tw:text-error-500',
  },
} as const

const Checkbox = ({
  control,
  name,
  onClick,
  children: labelContent,
  defaultChecked,
  errors,
  required = false,
  description,
  tone = 'primary',
  className,
}: CheckboxProps) => {
  const { control: fakeControl } = useForm()
  const { field } = useController({ name, control: control ?? fakeControl })
  const errorsById = _.get(errors, name) as FieldError | undefined
  const { ariaDescribedBy, descriptionId, errorId } = useFormField(name, errors, required, description)

  return (
    <>
      <RCheckbox.Root
        onClick={onClick}
        checked={field.value ?? defaultChecked}
        onCheckedChange={field.onChange}
        onBlur={field.onBlur}
        name={field.name}
        ref={field.ref}
        id={name}
        required={required}
        aria-describedby={ariaDescribedBy}
        className={clsx(
          'tw:flex tw:content-center tw:items-center tw:justify-center tw:cursor-pointer',
          'tw:w-4 tw:h-4 tw:p-2 tw:bg-white',
          'tw:border tw:rounded tw:border-gray-300',
          'tw:focus:outline-hidden tw:focus:ring-2',
          toneClasses[tone].box,
          errorsById && 'tw:border-red-400',
          className
        )}
      >
        <RCheckbox.Indicator>
          <Icon
            type="check"
            className={clsx('tw:stroke-2 tw:self-center tw:h-3 tw:w-3', toneClasses[tone].check)}
            data-testid="checked-icon"
          />
        </RCheckbox.Indicator>
      </RCheckbox.Root>
      <label
        htmlFor={name}
        className="tw:m-0 tw:text-sm tw:text-gray-700 tw:font-medium tw:pl-2 tw:max-w-[90%] tw:cursor-pointer"
      >
        {labelContent}
      </label>

      {/* This element helps force any errors/description onto their own line */}
      {(errorsById || description) && <div className="tw:basis-full tw:h-0"></div>}

      {/* Description text */}
      {description && (
        <p id={descriptionId} className="tw:text-gray-500 tw:text-sm tw:pt-1 tw:pl-6">
          {description}
        </p>
      )}

      {/* Show aria-live element on page load so content changes are reported */}
      {/* role="status" sets aria-live="polite" and aria-atomic="true" */}
      <div role="status" aria-relevant="all">
        {errorsById && (
          <p id={errorId} className="tw:text-red-600 tw:font-light tw:text-sm tw:pt-3">
            {errorsById.message}
          </p>
        )}
      </div>
    </>
  )
}

export default Checkbox
